import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDeleteDialog, StatusBadge, courseLabel, programStatusOptions } from '@/components/admin/AcademicTableHelpers';
import { deleteProgram, getCourseRefs, getPrograms, updateProgram } from '@/lib/api';
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  BookOpen,
  CalendarDays,
  Check,
  GraduationCap,
  Link as LinkIcon,
  Plus,
  RotateCcw,
  Save,
  Search,
  Trash2,
  Unlink,
  X,
} from 'lucide-react';

const emptyForm = {
  name: '',
  code: '',
  abbreviation: '',
  duration: '',
  prerequisite: '',
  invoice_price: '',
  field_of_study: '',
  training_type: '',
  level_of_study: '',
  description: '',
  status: 'active',
  course_refs: [],
};
const emptyOptionsForm = { pathway_enabled: false, pathway_description: '', french_questions_enabled: false, requires_enrolment_contract: false, attendance_modes: [], add_ons: [] };
const ATTENDANCE_MODE_OPTIONS = [
  { value: 'in_person', label: 'In-person' },
  { value: 'online_live', label: 'Online (Live)' },
  { value: 'online_self_paced', label: 'Online (Self-paced)' },
  { value: 'blended', label: 'Blended' },
];
const DEFAULT_ATTENDANCE_MODES = ATTENDANCE_MODE_OPTIONS.map((option) => option.value);
const ADD_ON_DURATION_UNITS = [
  { value: 'weeks', label: 'weeks' },
  { value: 'months', label: 'months' },
];
const DEFAULT_ADD_ON_DURATION_UNIT = 'weeks';
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const startDateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function safeAttendanceModes(value) {
  const allowed = new Set(DEFAULT_ATTENDANCE_MODES);
  const modes = safeList(value).filter((item) => allowed.has(item));
  return modes.length ? modes : DEFAULT_ATTENDANCE_MODES;
}

function safeAddOnDuration(duration) {
  const rawValue = duration?.value;
  const parsedValue = rawValue === '' || rawValue == null ? null : Number.parseInt(rawValue, 10);
  const value = Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  const unit = ADD_ON_DURATION_UNITS.some((option) => option.value === duration?.unit)
    ? duration.unit
    : DEFAULT_ADD_ON_DURATION_UNIT;
  return {
    value,
    unit: value ? unit : '',
  };
}

function safeAddOnPrice(price) {
  if (price === '' || price == null) return null;
  const parsed = Number.parseFloat(price);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : null;
}

function safeAddOns(value) {
  return safeList(value)
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      id: String(item.id || '').trim(),
      label: String(item.label || '').trim(),
      description: String(item.description || '').trim(),
      explanation: String(item.explanation || '').trim(),
      duration: safeAddOnDuration(item.duration),
      price: safeAddOnPrice(item.price),
      end_dates: safeList(item.end_dates)
        .filter((row) => row && typeof row === 'object' && ISO_DATE_PATTERN.test(String(row.start || '')))
        .map((row) => ({ start: String(row.start), end: String(row.end || '') })),
    }))
    .filter((item) => item.id);
}

function makeOptionsForm(program) {
  return {
    pathway_enabled: Boolean(program?.pathway_enabled),
    pathway_description: String(program?.pathway_description || ''),
    french_questions_enabled: Boolean(program?.french_questions_enabled),
    requires_enrolment_contract: Boolean(program?.requires_enrolment_contract),
    attendance_modes: safeAttendanceModes(program?.attendance_modes),
    add_ons: safeAddOns(program?.add_ons),
  };
}

function normalizeOptionsForCompare(options) {
  const selectedModes = new Set(safeAttendanceModes(options?.attendance_modes));
  return {
    pathway_enabled: Boolean(options?.pathway_enabled),
    pathway_description: options?.pathway_enabled ? String(options?.pathway_description || '').trim() : '',
    french_questions_enabled: Boolean(options?.french_questions_enabled),
    requires_enrolment_contract: Boolean(options?.requires_enrolment_contract),
    attendance_modes: DEFAULT_ATTENDANCE_MODES.filter((mode) => selectedModes.has(mode)),
    add_ons: safeAddOns(options?.add_ons).map((item) => ({
      id: item.id,
      label: item.label,
      description: item.description,
      explanation: item.explanation,
      duration: safeAddOnDuration(item.duration),
      price: safeAddOnPrice(item.price),
      end_dates: safeList(item.end_dates).map((row) => ({ start: row.start, end: row.end })),
    })),
  };
}

function makeAddOnId(label, existingIds) {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || `add-on-${Date.now()}`;
  let candidate = base;
  let index = 2;
  while (existingIds.has(candidate)) {
    candidate = `${base}-${index}`;
    index += 1;
  }
  return candidate;
}

function parseIsoDate(value) {
  if (typeof value !== 'string' || !ISO_DATE_PATTERN.test(value)) return null;

  // Adding a local-time suffix prevents UTC parsing from shifting dates for western time zones.
  const date = new Date(`${value}T00:00:00`);
  const [year, month, day] = value.split('-').map(Number);

  if (
    Number.isNaN(date.getTime())
    || date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function formatStartDate(value) {
  const date = parseIsoDate(value);
  return date ? startDateFormatter.format(date) : value;
}

// Mirrors PROGRAM_INTAKE_ENROLLMENT_LABELS in the backend. The website reads
// its wording from the API, so this list is the staff-facing half only.
const INTAKE_ENROLLMENT_OPTIONS = [
  { value: 'open', label: 'Open - Enroll Now', toast: 'Intake reopened for enrollment' },
  { value: 'closed', label: 'Closed - Enrolment Closed', toast: 'Intake closed for enrollment' },
  { value: 'full', label: 'Full - Fully Booked', toast: 'Intake marked fully booked' },
];

function programIntakeRows(program) {
  const endByStart = new Map();
  safeList(program?.intakes).forEach((item) => {
    if (typeof item === 'string') {
      const start = item.trim();
      if (start && !endByStart.has(start)) endByStart.set(start, { end: '', enrollment_status: 'open' });
      return;
    }
    if (!item || typeof item !== 'object') return;
    const start = String(item.start || '').trim();
    if (!start || endByStart.has(start)) return;
    endByStart.set(start, {
      end: String(item.end || '').trim(),
      enrollment_status: String(item.enrollment_status || 'open').trim() || 'open',
    });
  });

  return safeList(program?.semesters)
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .filter((start, index, starts) => starts.indexOf(start) === index)
    .map((start) => ({
      start,
      end: endByStart.get(start)?.end || '',
      enrollment_status: endByStart.get(start)?.enrollment_status || 'open',
    }))
    .sort((first, second) => first.start.localeCompare(second.start));
}

function intakePatch(rows) {
  const cleanRows = safeList(rows)
    .map((row) => ({
      start: String(row?.start || '').trim(),
      end: String(row?.end || '').trim(),
      enrollment_status: String(row?.enrollment_status || 'open').trim() || 'open',
    }))
    .filter((row) => row.start)
    .filter((row, index, values) => values.findIndex((item) => item.start === row.start) === index)
    .sort((first, second) => first.start.localeCompare(second.start));

  return {
    semesters: cleanRows.map((row) => row.start),
    intakes: cleanRows.map((row) => ({
      start: row.start,
      end: row.end || null,
      enrollment_status: row.enrollment_status,
    })),
  };
}

function hasEndBeforeStart(row) {
  const start = parseIsoDate(row?.start);
  const end = parseIsoDate(row?.end);
  return Boolean(start && end && end.getTime() < start.getTime());
}

function apiDetail(detail) {
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return 'Request failed';
}

export function makeForm(program) {
  return {
    name: program?.name || '',
    code: program?.code || '',
    abbreviation: String(program?.abbreviation || '').trim().toUpperCase(),
    duration: program?.duration || '',
    prerequisite: program?.prerequisite || '',
    invoice_price: program?.invoice_price ?? '',
    field_of_study: program?.field_of_study || '',
    training_type: program?.training_type || '',
    level_of_study: program?.level_of_study || '',
    description: program?.description || '',
    status: program?.status || 'active',
    course_refs: safeList(program?.course_refs),
  };
}

export function buildPatch(form, program) {
  const patch = {};
  const name = form.name.trim();
  const code = form.code.trim().toUpperCase();
  const abbreviation = form.abbreviation.trim().toUpperCase();
  const duration = form.duration.trim();
  const prerequisite = form.prerequisite.trim();
  const invoicePrice = safeAddOnPrice(form.invoice_price);
  const currentInvoicePrice = safeAddOnPrice(program.invoice_price);
  const fieldOfStudy = form.field_of_study.trim();
  const trainingType = form.training_type.trim();
  const levelOfStudy = form.level_of_study.trim();
  const description = form.description.trim();
  if (name !== (program.name || '')) patch.name = name;
  if (code !== (program.code || '')) patch.code = code;
  if (abbreviation !== (program.abbreviation || '')) {
    patch.abbreviation = abbreviation;
  }
  if (duration !== (program.duration || '')) patch.duration = duration;
  if (prerequisite !== (program.prerequisite || '')) patch.prerequisite = prerequisite;
  if (invoicePrice !== currentInvoicePrice) patch.invoice_price = invoicePrice;
  if (fieldOfStudy !== (program.field_of_study || '')) patch.field_of_study = fieldOfStudy;
  if (trainingType !== (program.training_type || '')) patch.training_type = trainingType;
  if (levelOfStudy !== (program.level_of_study || '')) patch.level_of_study = levelOfStudy;
  if (description !== (program.description || '')) patch.description = description;
  if (form.status !== (program.status || 'active')) patch.status = form.status;
  return patch;
}

export function hasRequiredProgramIdentity(form) {
  return Boolean(
    String(form?.name || '').trim()
    && String(form?.code || '').trim()
    && String(form?.abbreviation || '').trim(),
  );
}

function refsEqual(left, right) {
  return JSON.stringify([...safeList(left)].sort()) === JSON.stringify([...safeList(right)].sort());
}

export default function AdminProgramDetail() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [optionsForm, setOptionsForm] = useState(emptyOptionsForm);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const [renamePatch, setRenamePatch] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConflict, setDeleteConflict] = useState(null);
  const [courseSearch, setCourseSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [semesterSaving, setSemesterSaving] = useState(false);
  const [optionsSaving, setOptionsSaving] = useState(false);

  const courseById = useMemo(
    () => new Map(courses.map((course) => [course._id, course])),
    [courses],
  );

  const linkedRefs = useMemo(() => safeList(program?.course_refs), [program]);
  const linkedCourses = useMemo(
    () => linkedRefs.map((ref) => ({ ref, course: courseById.get(ref) })),
    [courseById, linkedRefs],
  );
  const linkedSet = useMemo(() => new Set(linkedRefs), [linkedRefs]);

  const intakeRows = useMemo(() => programIntakeRows(program), [program]);
  const startDates = useMemo(() => intakeRows.map((row) => row.start), [intakeRows]);
  const optionChanges = useMemo(() => {
    if (!program) return false;
    return JSON.stringify(normalizeOptionsForCompare(optionsForm)) !== JSON.stringify(normalizeOptionsForCompare(program));
  }, [optionsForm, program]);

  const applyProgram = useCallback((nextProgram) => {
    setProgram(nextProgram);
    setForm(makeForm(nextProgram));
    setOptionsForm(makeOptionsForm(nextProgram));
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setNotFound(false);
      const [programRes, courseRes] = await Promise.all([getPrograms(), getCourseRefs()]);
      const found = (programRes.data || []).find((item) => item.id === programId);
      if (!found) {
        setProgram(null);
        setNotFound(true);
      } else {
        applyProgram(found);
      }
      setCourses(courseRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error(apiDetail(err?.response?.data?.detail) || 'Could not load program');
    } finally {
      setLoading(false);
    }
  }, [applyProgram, programId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const availableCourses = useMemo(() => {
    const q = courseSearch.trim().toLowerCase();
    return courses
      .filter((course) => !linkedSet.has(course._id))
      .filter((course) => !activeOnly || course.status !== 'archived')
      .filter((course) => {
        if (!q) return true;
        return [course.title, course.course_code, course.status].join(' ').toLowerCase().includes(q);
      })
      .slice(0, 80);
  }, [activeOnly, courseSearch, courses, linkedSet]);

  const selectedCourse = useMemo(
    () => availableCourses.find((course) => course._id === selectedCourseId),
    [availableCourses, selectedCourseId],
  );

  useEffect(() => {
    if (selectedCourseId && !availableCourses.some((course) => course._id === selectedCourseId)) {
      setSelectedCourseId('');
    }
  }, [availableCourses, selectedCourseId]);

  const saveProgramPatch = async (patch, successMessage = 'Program updated') => {
    if (!program) return null;
    try {
      setSaving(true);
      const { data } = await updateProgram(program.id, patch);
      applyProgram(data.program);
      const cascade = data.cascade || {};
      if (patch.name) {
        toast.success(`${successMessage}. Cascade updated ${cascade.students_updated || 0} students and ${cascade.agents_updated || 0} agencies.`);
      } else {
        toast.success(successMessage);
      }
      return data.program;
    } catch (err) {
      console.error(err);
      toast.error(apiDetail(err?.response?.data?.detail) || 'Could not save program');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!program) return;
    if (!hasRequiredProgramIdentity(form)) {
      toast.error('Program name, academic code, and abbreviation are required');
      return;
    }
    const patch = buildPatch(form, program);
    if (Object.keys(patch).length === 0) {
      toast.info('No changes to save');
      return;
    }
    if (patch.name) {
      setRenamePatch(patch);
      return;
    }
    saveProgramPatch(patch);
  };

  const confirmRename = async () => {
    const patch = renamePatch;
    setRenamePatch(null);
    if (patch) await saveProgramPatch(patch, 'Program renamed');
  };

  const handleStatus = async (status) => {
    if (!program || program.status === status) return;
    await saveProgramPatch({ status }, status === 'archived' ? 'Program archived' : 'Program unarchived');
  };

  const updateCourseRefs = async (nextRefs, message) => {
    if (!program || refsEqual(nextRefs, program.course_refs)) return;
    try {
      setLinkSaving(true);
      const { data } = await updateProgram(program.id, { course_refs: nextRefs });
      applyProgram(data.program);
      toast.success(message);
    } catch (err) {
      console.error(err);
      toast.error(apiDetail(err?.response?.data?.detail) || 'Could not update linked courses');
    } finally {
      setLinkSaving(false);
    }
  };

  const saveProgramOptions = async () => {
    if (!program) return;
    const normalized = normalizeOptionsForCompare(optionsForm);
    if (!optionChanges) {
      toast.info('No program option changes to save');
      return;
    }
    try {
      setOptionsSaving(true);
      if (normalized.add_ons.some((item) => !item.label)) {
        toast.error('Add-on names cannot be blank');
        setOptionsSaving(false);
        return;
      }
      if (normalized.add_ons.some((item) => safeList(item.end_dates).some((row) => !row.end))) {
        toast.error('Add an ending date for every listed add-on starting date');
        setOptionsSaving(false);
        return;
      }
      const { data } = await updateProgram(program.id, normalized);
      applyProgram(data.program);
      toast.success('Program options updated');
    } catch (err) {
      console.error(err);
      toast.error(apiDetail(err?.response?.data?.detail) || 'Could not update program options');
    } finally {
      setOptionsSaving(false);
    }
  };

  const toggleAttendanceMode = (mode) => {
    setOptionsForm((prev) => {
      const current = safeAttendanceModes(prev.attendance_modes);
      const next = current.includes(mode)
        ? current.filter((item) => item !== mode)
        : [...current, mode];
      if (next.length === 0) {
        toast.error('At least one attendance mode must stay enabled');
        return prev;
      }
      return { ...prev, attendance_modes: next };
    });
  };

  const addAddOn = () => {
    setOptionsForm((prev) => {
      const existingIds = new Set(safeAddOns(prev.add_ons).map((item) => item.id));
      return {
        ...prev,
        add_ons: [
          ...safeAddOns(prev.add_ons),
          {
            id: makeAddOnId('New add-on', existingIds),
            label: 'New add-on',
            description: '',
            explanation: '',
            duration: { value: null, unit: '' },
            price: null,
            end_dates: [],
          },
        ],
      };
    });
  };

  const updateAddOn = (id, field, value) => {
    setOptionsForm((prev) => ({
      ...prev,
      add_ons: safeAddOns(prev.add_ons).map((item) => (
        item.id === id ? { ...item, [field]: value } : item
      )),
    }));
  };

  const updateAddOnDuration = (id, field, value) => {
    setOptionsForm((prev) => ({
      ...prev,
      add_ons: safeAddOns(prev.add_ons).map((item) => (
        item.id === id
          ? { ...item, duration: safeAddOnDuration({ ...item.duration, [field]: value }) }
          : item
      )),
    }));
  };

  const toggleAddOnEndDates = (id, enabled) => {
    setOptionsForm((prev) => ({
      ...prev,
      add_ons: safeAddOns(prev.add_ons).map((item) => (
        item.id === id
          ? {
            ...item,
            end_dates: enabled
              ? intakeRows.map((intake) => ({
                start: intake.start,
                end: safeList(item.end_dates).find((row) => row.start === intake.start)?.end || '',
              }))
              : [],
          }
          : item
      )),
    }));
  };

  const updateAddOnEndDate = (id, start, end) => {
    setOptionsForm((prev) => ({
      ...prev,
      add_ons: safeAddOns(prev.add_ons).map((item) => (
        item.id === id
          ? {
            ...item,
            end_dates: safeList(item.end_dates).map((row) => (
              row.start === start ? { ...row, end } : row
            )),
          }
          : item
      )),
    }));
  };

  const removeAddOn = (id) => {
    setOptionsForm((prev) => ({
      ...prev,
      add_ons: safeAddOns(prev.add_ons).filter((item) => item.id !== id),
    }));
  };

  const addCourse = async () => {
    if (!selectedCourse) {
      toast.error('Choose a course to link');
      return;
    }
    await updateCourseRefs([...linkedRefs, selectedCourse._id], 'Course linked to program');
    setSelectedCourseId('');
    setCourseSearch('');
  };

  const removeCourse = async (ref) => {
    await updateCourseRefs(linkedRefs.filter((item) => item !== ref), 'Course removed from program');
  };

  const updateIntakes = async (nextRows, message) => {
    if (!program) return;
    try {
      setSemesterSaving(true);
      const { data } = await updateProgram(program.id, intakePatch(nextRows));
      applyProgram(data.program);
      toast.success(message);
    } catch (err) {
      console.error(err);
      toast.error(apiDetail(err?.response?.data?.detail) || 'Could not update intake dates');
    } finally {
      setSemesterSaving(false);
    }
  };

  const addStartDate = async () => {
    const value = startDateInput.trim();
    if (!parseIsoDate(value)) {
      toast.error('Choose a valid start date');
      return;
    }
    if (startDates.includes(value)) {
      toast.error('That start date is already listed');
      return;
    }
    await updateIntakes([...intakeRows, { start: value, end: '' }], 'Intake added');
    setStartDateInput('');
  };

  const removeStartDate = async (value) => {
    await updateIntakes(intakeRows.filter((item) => item.start !== value), 'Intake removed');
  };

  const updateIntakeStart = async (currentStart, nextStart) => {
    const value = nextStart.trim();
    if (value === currentStart) return;
    if (!parseIsoDate(value)) {
      toast.error('Choose a valid start date');
      return;
    }
    if (intakeRows.some((row) => row.start !== currentStart && row.start === value)) {
      toast.error('That start date is already listed');
      return;
    }
    const nextRows = intakeRows.map((row) => (
      row.start === currentStart ? { ...row, start: value } : row
    ));
    const updatedRow = nextRows.find((row) => row.start === value);
    if (updatedRow?.end && hasEndBeforeStart(updatedRow)) {
      toast.warning('End date is before the start date. Saved as entered.');
    }
    await updateIntakes(nextRows, 'Intake start date updated');
  };

  const updateIntakeEnrollment = async (start, enrollment_status) => {
    await updateIntakes(
      intakeRows.map((row) => (row.start === start ? { ...row, enrollment_status } : row)),
      INTAKE_ENROLLMENT_OPTIONS.find((option) => option.value === enrollment_status)?.toast
        || 'Intake enrollment updated',
    );
  };

  const updateIntakeEnd = async (start, end) => {
    const nextRows = intakeRows.map((row) => (
      row.start === start ? { ...row, end } : row
    ));
    if (end && hasEndBeforeStart({ start, end })) {
      toast.warning('End date is before the start date. Saved as entered.');
    }
    await updateIntakes(nextRows, 'Intake end date updated');
  };

  const handleDelete = async () => {
    if (!program) return;
    try {
      setDeleting(true);
      await deleteProgram(program.id);
      toast.success('Program deleted');
      navigate('/admin/programs');
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && typeof detail === 'object') {
        setDeleteOpen(false);
        setDeleteConflict(detail);
      } else {
        toast.error(apiDetail(detail) || 'Could not delete program');
      }
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-44 rounded bg-[#F7F8FA] animate-pulse" />
        <div className="h-56 rounded-xl bg-[#F7F8FA] animate-pulse" />
        <div className="h-72 rounded-xl bg-[#F7F8FA] animate-pulse" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/admin/programs')} className="-ml-2 text-[#062B1A]">
          <ArrowLeft className="mr-2 h-4 w-4" /> Programs
        </Button>
        <div className="rounded-xl border border-red-100 bg-red-50 p-5 text-red-800">
          Program not found.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <Button variant="ghost" onClick={() => navigate('/admin/programs')} className="-ml-2 text-[#062B1A]">
            <ArrowLeft className="mr-2 h-4 w-4" /> Programs
          </Button>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#062B1A]/5">
              <GraduationCap className="h-5 w-5 text-[#062B1A]" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                  {program?.name}
                </h2>
                <StatusBadge value={program?.status || 'active'} />
              </div>
              <p className="text-sm text-[#062B1A]/55">
                <span className="font-semibold text-[#062B1A]">
                  {String(program?.abbreviation || '').trim().toUpperCase() || 'No abbreviation'}
                </span>
                {' · '}Academic code {program?.code || 'not set'}
                {' · '}{linkedRefs.length} linked course{linkedRefs.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="border-[#D4AF37]/40 text-[#8A6508] hover:bg-[#D4AF37]/5"
            disabled={saving}
            onClick={() => handleStatus(program.status === 'archived' ? 'active' : 'archived')}
          >
            {program.status === 'archived' ? <RotateCcw className="mr-2 h-4 w-4" /> : <Archive className="mr-2 h-4 w-4" />}
            {program.status === 'archived' ? 'Unarchive' : 'Archive'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <section className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Program Details
              </h3>
              <p className="mt-1 text-sm text-[#062B1A]/55">Rename, archive, and maintain the admin-facing program record.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Name *</label>
              <Input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Academic code *</label>
                <Input
                  value={form.code}
                  onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))}
                  aria-label="Program academic code"
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Abbreviation *</label>
                <Input
                  value={form.abbreviation}
                  onChange={(event) => setForm((prev) => ({ ...prev, abbreviation: event.target.value.toUpperCase() }))}
                  aria-label="Program abbreviation"
                  maxLength={24}
                  spellCheck={false}
                  placeholder="HO"
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Status</label>
                <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {programStatusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Duration</label>
                <Input
                  value={form.duration}
                  onChange={(event) => setForm((prev) => ({ ...prev, duration: event.target.value }))}
                  placeholder="2 academic years"
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Invoice price (CAD)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.invoice_price}
                  onChange={(event) => setForm((prev) => ({ ...prev, invoice_price: event.target.value }))}
                  placeholder="3900"
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Field of Study (LOA)</label>
                <Input
                  value={form.field_of_study}
                  onChange={(event) => setForm((prev) => ({ ...prev, field_of_study: event.target.value }))}
                  placeholder="Language"
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Training Type (LOA)</label>
                <Input
                  value={form.training_type}
                  onChange={(event) => setForm((prev) => ({ ...prev, training_type: event.target.value }))}
                  placeholder="Non-Academic"
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Level of Study (LOA)</label>
                <Input
                  value={form.level_of_study}
                  onChange={(event) => setForm((prev) => ({ ...prev, level_of_study: event.target.value }))}
                  placeholder="Certificate"
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Prerequisite</label>
              <Input
                value={form.prerequisite}
                onChange={(event) => setForm((prev) => ({ ...prev, prerequisite: event.target.value }))}
                placeholder="Not Required"
                className="border-[rgba(6,43,26,0.12)]"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Description</label>
              <Textarea value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} className="min-h-[160px] border-[rgba(6,43,26,0.12)]" />
            </div>
            <div className="flex justify-end">
              <Button className="bg-[#062B1A] text-white hover:bg-[#041B10]" onClick={handleSave} disabled={saving}>
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-white p-5 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Linked Courses
              </h3>
              <p className="mt-1 text-sm text-[#062B1A]/55">Courses can belong to multiple programs. Links store course Mongo IDs.</p>
            </div>
            <Badge variant="outline" className="w-fit border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#8A6508]">
              {linkedRefs.length} linked
            </Badge>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-3">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(220px,0.7fr)_auto]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#062B1A]/40" />
                  <Input
                    type="search"
                    aria-label="Search available courses"
                    value={courseSearch}
                    onChange={(event) => setCourseSearch(event.target.value)}
                    placeholder="Search by title or course code..."
                    className="border-[rgba(6,43,26,0.12)] pl-9"
                  />
                </div>
                <Select value={selectedCourseId || undefined} onValueChange={setSelectedCourseId}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)] bg-white"><SelectValue placeholder="Choose course" /></SelectTrigger>
                  <SelectContent>
                    {availableCourses.map((course) => (
                      <SelectItem key={course._id} value={course._id}>
                        {courseLabel(course)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" disabled={linkSaving || !selectedCourse} onClick={addCourse}>
                  <LinkIcon className="mr-2 h-4 w-4" />
                  Link
                </Button>
              </div>
              {availableCourses.length === 0 && (
                <p className="mt-2 text-xs text-[#062B1A]/50">No available courses match this search and filter.</p>
              )}
              <label className="mt-3 flex items-center gap-2 text-sm text-[#062B1A]/65">
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(event) => setActiveOnly(event.target.checked)}
                  className="h-4 w-4 rounded border-[#062B1A]/20"
                />
                Hide archived courses by default
              </label>
            </div>

            <div className="space-y-2">
              {linkedCourses.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[rgba(6,43,26,0.14)] p-6 text-center text-sm text-[#062B1A]/45">
                  No courses linked to this program yet.
                </div>
              ) : (
                linkedCourses.map(({ ref, course }) => (
                  <div key={ref} className="flex flex-col gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#062B1A]/5">
                        <BookOpen className="h-4 w-4 text-[#062B1A]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#062B1A]">{course ? courseLabel(course) : 'Unknown course'}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <StatusBadge value={course?.status || 'missing'} />
                          <span className="text-xs text-[#062B1A]/40">{ref}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={linkSaving}
                      className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                      onClick={() => removeCourse(ref)}
                    >
                      <Unlink className="mr-2 h-4 w-4" />
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <section className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-white p-5 shadow-sm">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#062B1A]/5">
              <Check className="h-4 w-4 text-[#062B1A]" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Program Options
              </h3>
              <p className="mt-1 max-w-xl text-sm text-[#062B1A]/55">
                Control program-specific choices shown on the website application form.
              </p>
            </div>
          </div>
          {optionChanges && (
            <Badge variant="outline" className="w-fit border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#8A6508]">
              Unsaved
            </Badge>
          )}
        </div>

        <div className="space-y-5">
          <label className="flex items-start gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-3 text-sm text-[#062B1A]">
            <input
              type="checkbox"
              checked={optionsForm.pathway_enabled}
              onChange={(event) => setOptionsForm((prev) => ({ ...prev, pathway_enabled: event.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-[#062B1A]/20"
            />
            <span>
              <span className="block font-medium">This program offers a pathway.</span>
              <span className="mt-1 block text-[#062B1A]/55">When on, applicants see a Yes / No / Not-decided-yet question.</span>
            </span>
          </label>

          {optionsForm.pathway_enabled && (
            <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
                Which pathway?
              </p>
              <Input
                value={optionsForm.pathway_description}
                onChange={(event) => setOptionsForm((prev) => ({ ...prev, pathway_description: event.target.value }))}
                placeholder="e.g. University Transfer Pathway"
                className="border-[rgba(6,43,26,0.16)] bg-white"
              />
              <p className="mt-1 text-[11px] text-[#062B1A]/45">
                Printed on the LOA as the Pathway Opportunity. Programs without a pathway print N/A.
              </p>
            </div>
          )}

          <label className="flex items-start gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-3 text-sm text-[#062B1A]">
            <input
              type="checkbox"
              checked={optionsForm.french_questions_enabled}
              onChange={(event) => setOptionsForm((prev) => ({ ...prev, french_questions_enabled: event.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-[#062B1A]/20"
            />
            <span>
              <span className="block font-medium">Ask the French intake questions.</span>
              <span className="mt-1 block text-[#062B1A]/55">
                When on, applicants are asked their goal for learning French and their current
                level, and can upload a TCF/TEF result or ask for a placement test. On by default
                for French programs.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-3 text-sm text-[#062B1A]">
            <input
              type="checkbox"
              checked={optionsForm.requires_enrolment_contract}
              onChange={(event) => setOptionsForm((prev) => ({ ...prev, requires_enrolment_contract: event.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-[#062B1A]/20"
            />
            <span>
              <span className="block font-medium">Student Enrolment Contract required.</span>
              <span className="mt-1 block text-[#062B1A]/55">When on, Offered applications automatically receive this editable contract.</span>
            </span>
          </label>

          <div className="space-y-2">
            <p className="text-sm font-medium text-[#062B1A]/70">Attendance modes</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ATTENDANCE_MODE_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2 rounded-lg border border-[rgba(6,43,26,0.08)] px-3 py-2 text-sm text-[#062B1A]">
                  <input
                    type="checkbox"
                    checked={safeAttendanceModes(optionsForm.attendance_modes).includes(option.value)}
                    onChange={() => toggleAttendanceMode(option.value)}
                    className="h-4 w-4 rounded border-[#062B1A]/20"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-[#062B1A]/70">Optional add-ons</p>
              <Button type="button" variant="outline" size="sm" onClick={addAddOn} className="border-[#D4AF37]/40 text-[#8A6508] hover:bg-[#D4AF37]/5">
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </div>
            {safeAddOns(optionsForm.add_ons).length === 0 ? (
              <div className="rounded-lg border border-dashed border-[rgba(6,43,26,0.14)] p-5 text-center text-sm text-[#062B1A]/45">
                No optional add-ons configured.
              </div>
            ) : (
              <div className="space-y-3">
                {safeAddOns(optionsForm.add_ons).map((addOn) => (
                  <div key={addOn.id} className="rounded-lg border border-[rgba(6,43,26,0.08)] p-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#062B1A]/55">Label</label>
                        <Input
                          value={addOn.label}
                          onChange={(event) => updateAddOn(addOn.id, 'label', event.target.value)}
                          placeholder="Add-on name"
                          className="border-[rgba(6,43,26,0.12)]"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeAddOn(addOn.id)}
                        className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                        aria-label={`Remove ${addOn.label || 'add-on'}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(116px,0.8fr)_minmax(116px,0.75fr)_minmax(132px,1fr)]">
                      <div>
                        <label className="mb-1.5 flex min-h-4 items-end whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-[#062B1A]/55">Structured duration</label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={addOn.duration.value || ''}
                          onChange={(event) => updateAddOnDuration(addOn.id, 'value', event.target.value)}
                          placeholder="12"
                          className="border-[rgba(6,43,26,0.12)]"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 flex min-h-4 items-end whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-[#062B1A]/55">Unit</label>
                        <select
                          value={addOn.duration.unit}
                          onChange={(event) => updateAddOnDuration(addOn.id, 'unit', event.target.value)}
                          className="h-10 w-full rounded-md border border-[rgba(6,43,26,0.12)] bg-white px-3 py-2 text-sm text-[#062B1A] outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20"
                        >
                          <option value="">Select</option>
                          {ADD_ON_DURATION_UNITS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1.5 flex min-h-4 items-end whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-[#062B1A]/55">Invoice price (CAD)</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={addOn.price ?? ''}
                          onChange={(event) => updateAddOn(addOn.id, 'price', event.target.value)}
                          placeholder="1300"
                          className="border-[rgba(6,43,26,0.12)]"
                        />
                      </div>
                    </div>
                    <div className="mt-3 rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-3">
                      <label className="flex items-center gap-2 text-sm font-medium text-[#062B1A]">
                        <input
                          type="checkbox"
                          checked={safeList(addOn.end_dates).length > 0}
                          onChange={(event) => toggleAddOnEndDates(addOn.id, event.target.checked)}
                          disabled={intakeRows.length === 0}
                          className="h-4 w-4 rounded border-[#062B1A]/20"
                        />
                        This extra has its own ending dates
                      </label>
                      {intakeRows.length === 0 && (
                        <p className="mt-1 text-xs text-[#062B1A]/50">Add program intake dates first.</p>
                      )}
                      {safeList(addOn.end_dates).length > 0 && (
                        <div className="mt-3 space-y-2">
                          {safeList(addOn.end_dates).map((row) => (
                            <div key={row.start} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                              <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#062B1A]/50">Starting date</label>
                                <Input type="date" value={row.start} disabled className="border-[rgba(6,43,26,0.10)] bg-white" />
                              </div>
                              <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#062B1A]/50">Extra ending date</label>
                                <Input
                                  type="date"
                                  min={row.start}
                                  value={row.end}
                                  onChange={(event) => updateAddOnEndDate(addOn.id, row.start, event.target.value)}
                                  className="border-[rgba(6,43,26,0.12)] bg-white"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-[#062B1A]/55">Marketing form description</label>
                    <Textarea
                      value={addOn.description}
                      onChange={(event) => updateAddOn(addOn.id, 'description', event.target.value)}
                      placeholder="Optional description shown on the application form"
                      className="mt-3 min-h-[72px] border-[rgba(6,43,26,0.12)]"
                    />
                    <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-[#062B1A]/55">Offer letter explanation</label>
                    <Textarea
                      value={addOn.explanation}
                      onChange={(event) => updateAddOn(addOn.id, 'explanation', event.target.value)}
                      placeholder="Explanation printed in the offer letter"
                      className="mt-3 min-h-[84px] border-[rgba(6,43,26,0.12)]"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button className="bg-[#062B1A] text-white hover:bg-[#041B10]" onClick={saveProgramOptions} disabled={optionsSaving || !optionChanges}>
              <Save className="mr-2 h-4 w-4" />
              {optionsSaving ? 'Saving...' : 'Save Program Options'}
            </Button>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-white p-5 shadow-sm">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#062B1A]/5">
              <CalendarDays className="h-4 w-4 text-[#062B1A]" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Intake Dates
              </h3>
              <p className="mt-1 max-w-xl text-sm text-[#062B1A]/55">
                Add one date for each intake offered for this program on the website application form, with an optional end date for schedules and offer letters.
                &ldquo;As soon as possible&rdquo; is always offered automatically &mdash; you don&apos;t need to add it.
              </p>
            </div>
          </div>
          <Badge variant="outline" className="w-fit border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#8A6508]">
            {startDates.length} intake{startDates.length === 1 ? '' : 's'}
          </Badge>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
              <Input
                type="date"
                value={startDateInput}
                onChange={(event) => setStartDateInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addStartDate();
                  }
                }}
                className="border-[rgba(6,43,26,0.12)] bg-white"
              />
              <Button
                className="bg-[#D4AF37] text-black hover:bg-[#B8941F]"
                disabled={semesterSaving || !parseIsoDate(startDateInput)}
                onClick={addStartDate}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </div>
          </div>

          {startDates.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[rgba(6,43,26,0.14)] p-6 text-center text-sm text-[#062B1A]/45">
              No intake start dates configured. The form will offer only &ldquo;As soon as possible&rdquo; for this program.
            </div>
          ) : (
            <div className="space-y-2">
              {intakeRows.map((row) => (
                <div
                  key={row.start}
                  className="grid grid-cols-1 gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] bg-white p-3 sm:grid-cols-[minmax(160px,1fr)_minmax(160px,1fr)_minmax(170px,1fr)_auto]"
                >
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-[0.08em] text-[#062B1A]/45" htmlFor={`intake-start-${row.start}`}>
                      Start
                    </label>
                    <Input
                      id={`intake-start-${row.start}`}
                      type="date"
                      value={row.start}
                      onChange={(event) => updateIntakeStart(row.start, event.target.value)}
                      className={`border-[rgba(6,43,26,0.12)] ${hasEndBeforeStart(row) ? 'border-[#D4AF37] bg-[#D4AF37]/5' : ''}`}
                      disabled={semesterSaving}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-[0.08em] text-[#062B1A]/45" htmlFor={`intake-end-${row.start}`}>
                      End
                    </label>
                    <Input
                      id={`intake-end-${row.start}`}
                      type="date"
                      value={row.end || ''}
                      onChange={(event) => updateIntakeEnd(row.start, event.target.value)}
                      className={`border-[rgba(6,43,26,0.12)] ${hasEndBeforeStart(row) ? 'border-[#D4AF37] bg-[#D4AF37]/5' : ''}`}
                      disabled={semesterSaving}
                    />
                    {hasEndBeforeStart(row) && (
                      <p className="mt-1 text-xs text-[#8A6508]">End date is before start.</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-[0.08em] text-[#062B1A]/45">
                      Website enrollment
                    </label>
                    <Select
                      value={row.enrollment_status || 'open'}
                      onValueChange={(value) => updateIntakeEnrollment(row.start, value)}
                      disabled={semesterSaving}
                    >
                      <SelectTrigger className="border-[rgba(6,43,26,0.12)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INTAKE_ENROLLMENT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={semesterSaving}
                    onClick={() => removeStartDate(row.start)}
                    className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                    aria-label={`Remove ${formatStartDate(row.start)}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      </div>

      <AlertDialog open={!!renamePatch} onOpenChange={(open) => !open && setRenamePatch(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Rename program?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Renaming propagates to every student and agency referencing this program. The save response will report how many records were updated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={saving} onClick={confirmRename} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              <Check className="mr-2 h-4 w-4" />
              Confirm Rename
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${program?.name || 'program'}?`}
        description="This permanently removes the program only when no students or agencies reference it. Archive is the safer action for active records."
        count={1}
        deleting={deleting}
        onConfirm={handleDelete}
      />

      <AlertDialog open={!!deleteConflict} onOpenChange={(open) => !open && setDeleteConflict(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              <AlertTriangle className="h-5 w-5 text-[#D4AF37]" />
              Archive this program instead?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(deleteConflict?.students || 0)} students and {(deleteConflict?.agents || 0)} agencies reference this program. Archive it to remove it from active pickers while preserving those records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={async () => {
                await handleStatus('archived');
                setDeleteConflict(null);
              }}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              Archive program
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
